import { clearAccessToken, setAccessToken } from '../api/auth-tokens.js';
import { ApiError } from '../api/errors.js';
import {
  type IssuedAccess,
  signInWithPassword as postLogin,
  refreshAccess,
  signOutOfSession,
} from '../features/auth/api.js';
import { type SessionStatus, useSessionStore } from './session.store.js';

/**
 * How long before the access token expires the workspace replaces it.
 *
 * The token lasts ten minutes. A minute of margin covers a slow network and a tab the browser
 * throttled, and it means the gateway never sees an expired token — it closes a socket whose token
 * has run out, and reconnecting mid-call to recover from a clock is a cost with no reason.
 */
export const refreshMarginMs = 60_000;

/** The session lifecycle, in one place: the token in memory, the timer, and the store. */
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

function hold(issued: IssuedAccess): number {
  const expiresAt = Date.now() + issued.expiresIn * 1000;
  setAccessToken(issued.accessToken);
  useSessionStore.getState().signedIn(expiresAt);
  return expiresAt;
}

function release(): void {
  clearAccessToken();
  stopRefreshing();
  useSessionStore.getState().signedOut();
}

/**
 * Recovers a session on load.
 *
 * The access token is gone — it was only ever in memory — and the refresh cookie is the only thing
 * that survived, so every reload starts by spending it. A refusal is the ordinary answer for a
 * visitor who is not signed in, and is not an error to show.
 */
export async function restoreSession(): Promise<SessionStatus> {
  // Two restores at once would present the same cookie twice, and the refresh token is rotating
  // with reuse detection: the second one would look like a stolen token and revoke the whole
  // family. React mounting an effect twice in development is enough to do it, so callers share
  // whichever restore is already in flight.
  restoring ??= attemptRestore().finally(() => {
    restoring = undefined;
  });
  return restoring;
}

let restoring: Promise<SessionStatus> | undefined;

async function attemptRestore(): Promise<SessionStatus> {
  try {
    hold(await refreshAccess());
    return 'authenticated';
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      release();
      return 'anonymous';
    }
    // Anything else — the API is down, the network is gone — is not "signed out". Saying so would
    // send somebody to the sign-in page to be refused there too.
    release();
    throw error;
  }
}

/**
 * The session status, restoring it first if nobody has tried yet.
 *
 * What a route guard asks. A tab that has just loaded is `unknown` rather than `anonymous` — the
 * refresh token is a cookie the script cannot read — so the guard has to spend the cookie before it
 * can answer, and every later navigation gets the answer for nothing.
 */
export async function currentOrRestoredStatus(): Promise<SessionStatus> {
  const status = useSessionStore.getState().status;
  if (status !== 'unknown') {
    return status;
  }
  try {
    return await restoreSession();
  } catch {
    // The API is unreachable rather than refusing. Treated as no session, because there is nothing
    // else a guard can do with it; the sign-in page will say what went wrong when it is tried.
    return 'anonymous';
  }
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  hold(await postLogin(email, password));
}

/**
 * Ends the session.
 *
 * The local state goes whatever the server said: a caller asking to be signed out is signed out,
 * and leaving a token in memory because the logout call failed is the one outcome nobody wants.
 */
export async function signOut(): Promise<void> {
  try {
    await signOutOfSession();
  } finally {
    release();
  }
}

/**
 * Keeps the access token fresh, and says when it has been replaced.
 *
 * `onRefreshed` is how the gateway hears about it: the socket re-authenticates with a control frame
 * rather than reconnecting, so a refresh costs nothing on an open call. A refresh that fails ends
 * the session — the refresh token is rotating and session-bound, so a refusal means the session is
 * over, not that the request should be retried.
 */
export function startRefreshing(
  expiresAt: number | undefined,
  onRefreshed: () => void,
  onEnded: () => void,
): () => void {
  const schedule = (next: number | undefined) => {
    stopRefreshing();
    if (next === undefined) {
      return;
    }
    refreshTimer = setTimeout(
      () => {
        void (async () => {
          try {
            schedule(hold(await refreshAccess()));
            onRefreshed();
          } catch {
            release();
            onEnded();
          }
        })();
      },
      Math.max(0, next - Date.now() - refreshMarginMs),
    );
  };

  schedule(expiresAt);
  return stopRefreshing;
}

export function stopRefreshing(): void {
  if (refreshTimer !== undefined) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }
}
